import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { doc, getDoc, updateDoc } from "firebase/firestore";

import {
  PersonalSettings as PersonalSettingsData,
  User
} from "../../../../shared/types";
import { userPath } from "../../../../shared/database";
import { firestore } from "../../plugins/firebase";
import { LocalSettings, SettingsKey } from "../../plugins/localSettings";

type PersonalSettingKey = keyof PersonalSettingsData;

export const PERSONAL_SETTING_KEYS: PersonalSettingKey[] = [
  "diff_mode",
  "sort_order",
  "diff_server",
  "theme",
  "auto_add_requested_reviews"
];

export const DEFAULT_PERSONAL_SETTINGS: Required<PersonalSettingsData> = {
  diff_mode: "split",
  sort_order: "diff-desc",
  diff_server: "github",
  theme: "dark",
  auto_add_requested_reviews: false
};

const LEGACY_SETTINGS: Array<{
  key: PersonalSettingKey;
  valid: (value: string) => boolean;
}> = [
  {
    key: "diff_mode",
    valid: value => value === "split" || value === "unified"
  },
  {
    key: "sort_order",
    valid: value =>
      value === "diff-desc" || value === "diff-asc" || value === "name"
  },
  {
    key: "diff_server",
    valid: value => value === "github" || value === "codeapprove"
  },
  {
    key: "theme",
    valid: value => value === "dark" || value === "light"
  }
];

function getLegacyKey<T>(key: PersonalSettingKey): SettingsKey<T> | undefined {
  switch (key) {
    case "diff_mode":
      return LocalSettings.KEY_DIFF_MODE;
    case "sort_order":
      return LocalSettings.KEY_SORT_ORDER;
    case "diff_server":
      return LocalSettings.KEY_DIFF_SERVER;
    case "theme":
      return LocalSettings.KEY_THEME;
    case "auto_add_requested_reviews":
      return undefined;
  }
}

function getLegacyValue<T>(key: PersonalSettingKey, defaultValue: T): T {
  const legacyKey = getLegacyKey(key);
  if (!legacyKey) {
    return defaultValue;
  }

  return LocalSettings.getOrDefault(legacyKey, defaultValue) as T;
}

export function getPersonalSetting<T>(
  settings: PersonalSettingsData,
  key: PersonalSettingKey,
  defaultValue: T
): T {
  const value = (settings[key] as unknown) as T | undefined;
  if (value !== undefined) {
    return value;
  }

  return getLegacyValue(key, defaultValue);
}

async function updatePersonalSettings(
  uid: string,
  settings: Partial<PersonalSettingsData>
) {
  const updates: Record<string, any> = {};
  for (const key of Object.keys(settings)) {
    const value = settings[key as PersonalSettingKey];
    if (value !== undefined) {
      updates[`personal_settings.${key}`] = value;
    }
  }

  await updateDoc(doc(firestore(), userPath({ id: uid })), updates);
}

function getLegacySettings() {
  const settings: PersonalSettingsData = {};
  const localKeys: string[] = [];

  for (const legacySetting of LEGACY_SETTINGS) {
    const legacyKey = getLegacyKey(legacySetting.key);
    if (!legacyKey) {
      continue;
    }

    const value = localStorage.getItem(legacyKey);
    if (value !== null && legacySetting.valid(value)) {
      (settings as Record<string, string>)[legacySetting.key] = value;
      localKeys.push(legacyKey);
    }
  }

  return { settings, localKeys };
}

function getMissingSettings(
  settings: PersonalSettingsData,
  legacySettings: PersonalSettingsData,
  excludedKey?: PersonalSettingKey
) {
  const missing: PersonalSettingsData = {};
  for (const key of PERSONAL_SETTING_KEYS) {
    if (
      key !== excludedKey &&
      settings[key] === undefined &&
      legacySettings[key] !== undefined
    ) {
      (missing as Record<string, unknown>)[key] = legacySettings[key];
    }
  }

  return missing;
}

function removeLegacySettings(localKeys: string[]) {
  for (const localKey of localKeys) {
    LocalSettings.remove(localKey);
  }
}

@Module({
  name: "personalSettings"
})
export default class PersonalSettingsModule extends VuexModule {
  public uid: string | null = null;
  public settings: PersonalSettingsData = {};
  public loaded = false;

  get value() {
    return <T>(opts: { key: PersonalSettingKey; defaultValue: T }): T => {
      return getPersonalSetting(this.settings, opts.key, opts.defaultValue);
    };
  }

  get all(): PersonalSettingsData {
    const settings: PersonalSettingsData = {};
    for (const key of PERSONAL_SETTING_KEYS) {
      const value = getPersonalSetting(
        this.settings,
        key,
        undefined as unknown
      );
      if (value !== undefined) {
        (settings as Record<string, unknown>)[key] = value;
      }
    }

    return settings;
  }

  @Mutation
  setSettings(opts: { uid: string; settings: PersonalSettingsData }) {
    this.uid = opts.uid;
    this.settings = opts.settings;
    this.loaded = true;
  }

  @Mutation
  setSetting(opts: { key: PersonalSettingKey; value: unknown }) {
    this.settings = {
      ...this.settings,
      [opts.key]: opts.value
    };
  }

  @Mutation
  reset() {
    this.uid = null;
    this.settings = {};
    this.loaded = false;
  }

  @Action({ rawError: true })
  async load(opts: { uid: string }) {
    const user = (
      await getDoc(doc(firestore(), userPath({ id: opts.uid })))
    ).data() as User | undefined;
    let settings = { ...(user?.personal_settings || {}) };
    const legacy = getLegacySettings();
    const migration = getMissingSettings(settings, legacy.settings);

    try {
      if (Object.keys(migration).length > 0) {
        await updatePersonalSettings(opts.uid, migration);
        settings = { ...settings, ...migration };
      }
      removeLegacySettings(legacy.localKeys);
    } catch (e) {
      // The local values remain available through the value getter.
      console.warn("Failed to migrate personal settings", e);
    }

    this.context.commit("setSettings", {
      uid: opts.uid,
      settings
    });
  }

  @Action({ rawError: true })
  async set(opts: { key: PersonalSettingKey; value: unknown }) {
    if (!this.uid) {
      throw new Error("Cannot update personal settings while signed out");
    }

    const legacy = getLegacySettings();
    const settingsToUpdate: Partial<PersonalSettingsData> = {
      ...getMissingSettings(this.settings, legacy.settings, opts.key),
      [opts.key]: opts.value
    };

    await updatePersonalSettings(this.uid, settingsToUpdate);
    this.context.commit("setSettings", {
      uid: this.uid,
      settings: {
        ...this.settings,
        ...settingsToUpdate
      }
    });
    removeLegacySettings(legacy.localKeys);
  }
}
